import { useState, useEffect } from 'react';
import { useMeetingStore } from '@/lib/meeting-store';
import { useOwnerStore } from '@/lib/owner-store';
import { supabase } from '@/lib/supabase';
import { Calendar } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { DateInput } from '@/components/ui/date-input';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import * as z from 'zod';
import type { OneOnOneMeeting } from '@/lib/types';

const formSchema = z.object({
  team_member_id: z.string().min(1, 'Please select a team member'),
  facilitator_id: z.string().min(1, 'Please select a facilitator'),
  meeting_date: z.string().min(1, 'Please select a date'),
  duration_minutes: z.coerce
    .number()
    .min(15, 'Minimum duration is 15 minutes')
    .max(180, 'Maximum duration is 180 minutes'),
});

type FormData = z.infer<typeof formSchema>;

interface MeetingFormProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess?: (meeting: OneOnOneMeeting) => void;
}

export function MeetingForm({ open, onOpenChange, onSuccess }: MeetingFormProps) {
  const { owners, fetchOwners } = useOwnerStore();
  const { createMeeting } = useMeetingStore();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const form = useForm<FormData>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      duration_minutes: 30,
    },
  });

  // Fetch owners when component mounts
  useEffect(() => {
    fetchOwners();
  }, [fetchOwners]);

  useEffect(() => {
    const getCurrentUser = async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (user) {
        // Get the task owner ID for this user
        const { data: taskOwner } = await supabase
          .from('task_owners')
          .select('id')
          .eq('user_id', user.id)
          .single();

        if (taskOwner) {
          form.setValue('facilitator_id', taskOwner.id);
        }
      }
    };
    getCurrentUser();
  }, [form]);

  const onSubmit = async (data: FormData) => {
    try {
      setIsSubmitting(true);
      setError(null);
      
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        throw new Error('No authenticated user found');
      }

      // Get the task owner ID for this user
      const { data: taskOwner } = await supabase
        .from('task_owners')
        .select('id')
        .eq('user_id', user.id)
        .single();

      if (!taskOwner) {
        throw new Error('User is not registered as a task owner');
      }

      const meeting = await createMeeting({
        ...data,
        facilitator_id: taskOwner.id,
        status: 'scheduled',
      });
      
      form.reset();
      onOpenChange(false);
      onSuccess?.(meeting);
    } catch (error) {
      console.error('Failed to create meeting:', error);
      setError(error instanceof Error ? error.message : 'Failed to create meeting');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Calendar className="w-5 h-5" />
            Schedule New Meeting
          </DialogTitle>
        </DialogHeader>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            {error && (
              <div className="text-sm font-medium text-red-600 dark:text-red-500">
                {error}
              </div>
            )}
            <FormField
              control={form.control}
              name="team_member_id"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Team Member</FormLabel>
                  <Select
                    onValueChange={field.onChange}
                    defaultValue={field.value}
                  >
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder="Select team member" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {owners.map((owner) => (
                        <SelectItem key={owner.id} value={owner.id}>
                          {owner.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="facilitator_id"
              render={({ field }) => (
                <FormItem className="hidden">
                  <FormControl>
                    <Input type="hidden" {...field} />
                  </FormControl>
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="meeting_date"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Meeting Date</FormLabel>
                  <FormControl>
                    <DateInput
                      {...field}
                      min={new Date().toISOString().split('T')[0]}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="duration_minutes"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Duration (minutes)</FormLabel>
                  <FormControl>
                    <Input
                      type="number"
                      min={15}
                      max={180}
                      step={15}
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <div className="flex justify-end space-x-2 pt-4">
              <Button
                type="button"
                variant="outline"
                onClick={() => onOpenChange(false)}
                disabled={isSubmitting}
              >
                Cancel
              </Button>
              <Button type="submit" disabled={isSubmitting}>
                {isSubmitting ? 'Creating...' : 'Create Meeting'}
              </Button>
            </div>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
